/*
==============================================================================
MANTRAROMA CUSTOM
Rabattfortschritt

Version: 0.7.0
Datum: 12.07.2026

- Erkennt rabattfähige Varianten über custom.rabatt = "neu"
- Liest den aktuellen Rabattstatus des Warenkorbs
- Aktualisiert Produktseite, Cart Drawer und normale Warenkorbseite
- Öffnet die Produktseiten-Box automatisch bei Rabattvarianten
- Öffnet die Produktseiten-Box automatisch bei Aktionsartikeln im Warenkorb
- Reagiert automatisch auf AJAX-Warenkorbänderungen
- Nutzt eine gemeinsame zentrale Logik für alle Anzeigen
- Rabattblock im Warenkorb ist zuklappbar
- Zustand des Warenkorbblocks wird im Browser gespeichert
==============================================================================
*/

(() => {
  const SELECTORS = {
    productProgress:
      '[data-mantraroma-discount-progress-product]',
    productVariants:
      '[data-mantraroma-discount-variants]',
    productText:
      '[data-mantraroma-discount-progress-text]',
    productToggle:
      '[data-mantraroma-discount-toggle]',
    productContent:
      '[data-mantraroma-discount-content]',
    variantInput:
      'form[data-mantraroma-product-form] input[name="id"]',

    cartProgress:
      '[data-mantraroma-discount-progress-cart]',
    cartText:
      '[data-mantraroma-discount-progress-cart-text]',
    cartToggle:
      '[data-mantraroma-discount-cart-toggle]',
    cartContent:
      '[data-mantraroma-discount-cart-content]'
  };

  const CART_COLLAPSE_STORAGE_KEY =
    'mantraromaDiscountCartCollapsed';

  let cartDiscountQuantity = 0;
  let refreshRequest = null;
  let productProgressInstances = [];

  function getStoredCartCollapsedState() {
    try {
      return (
        window.localStorage.getItem(
          CART_COLLAPSE_STORAGE_KEY
        ) === 'true'
      );
    } catch (error) {
      return false;
    }
  }

  function storeCartCollapsedState(isCollapsed) {
    try {
      window.localStorage.setItem(
        CART_COLLAPSE_STORAGE_KEY,
        String(isCollapsed)
      );
    } catch (error) {
      // Der Rabattblock funktioniert auch ohne localStorage weiter.
    }
  }

  function applyCartCollapsedState() {
    const isCollapsed =
      getStoredCartCollapsedState();

    document
      .querySelectorAll(SELECTORS.cartProgress)
      .forEach((progressElement) => {
        const toggleElement =
          progressElement.querySelector(
            SELECTORS.cartToggle
          );

        const contentElement =
          progressElement.querySelector(
            SELECTORS.cartContent
          );

        progressElement.classList.toggle(
          'is-collapsed',
          isCollapsed
        );

        if (toggleElement) {
          toggleElement.setAttribute(
            'aria-expanded',
            String(!isCollapsed)
          );
        }

        if (contentElement) {
          contentElement.setAttribute(
            'aria-hidden',
            String(isCollapsed)
          );
        }
      });
  }

  function handleCartToggleClick(event) {
    const toggleElement = event.target.closest(
      SELECTORS.cartToggle
    );

    if (!toggleElement) {
      return;
    }

    const progressElement = toggleElement.closest(
      SELECTORS.cartProgress
    );

    if (!progressElement) {
      return;
    }

    const isCurrentlyCollapsed =
      progressElement.classList.contains(
        'is-collapsed'
      );

    storeCartCollapsedState(
      !isCurrentlyCollapsed
    );

    applyCartCollapsedState();
  }

  async function fetchDiscountState() {
    const response = await fetch(
      `/cart?section_id=mantraroma-discount-state&_=${Date.now()}`,
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Rabattstatus konnte nicht geladen werden: ${response.status}`
      );
    }

    const html = await response.text();

    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const stateElement =
      parsedDocument.querySelector(
        '[data-mantraroma-discount-state]'
      );

    if (!stateElement) {
      throw new Error(
        'Rabattstatus wurde in der Section nicht gefunden.'
      );
    }

    const state = JSON.parse(
      stateElement.textContent.trim()
    );

    return Number(state.quantity) || 0;
  }

  function getProgressText(quantity) {
    if (quantity >= 5) {
      return 'Du hast den maximalen Rabatt von 50 % erreicht.';
    }

    if (quantity === 4) {
      return '20 % Rabatt erreicht – noch 1 💜-Artikel bis 50 %.';
    }

    if (quantity === 3) {
      return '20 % Rabatt erreicht – noch 2 💜-Artikel bis 50 %.';
    }

    if (quantity === 2) {
      return 'Noch 1 💜-Artikel bis 20 % Rabatt.';
    }

    if (quantity === 1) {
      return 'Noch 2 💜-Artikel bis 20 % Rabatt.';
    }

    return 'Spare 20 % ab 3 💜-Artikeln und 50 % ab 5 💜-Artikeln.';
  }

  function createProductProgress(progressElement) {
    const variantsElement =
      progressElement.querySelector(
        SELECTORS.productVariants
      );

    const textElement =
      progressElement.querySelector(
        SELECTORS.productText
      );

    const toggleElement =
      progressElement.querySelector(
        SELECTORS.productToggle
      );

    const contentElement =
      progressElement.querySelector(
        SELECTORS.productContent
      );

    const productForm =
      progressElement.closest('product-form');

    if (
      !variantsElement ||
      !textElement ||
      !toggleElement ||
      !contentElement ||
      !productForm
    ) {
      return null;
    }

    const variantInput =
      productForm.querySelector(
        SELECTORS.variantInput
      );

    if (!variantInput) {
      return null;
    }

    let variantMap;
    let manuallyClosed = false;

    try {
      variantMap = JSON.parse(
        variantsElement.textContent.trim()
      );
    } catch (error) {
      console.error(
        'MANTRAROMA Rabattfortschritt: Variantendaten sind ungültig.',
        error
      );

      return null;
    }

    function setOpen(isOpen) {
      toggleElement.setAttribute(
        'aria-expanded',
        String(isOpen)
      );

      if (isOpen) {
        contentElement.hidden = false;
        contentElement.style.height = '0px';

        requestAnimationFrame(() => {
          contentElement.classList.add('is-open');
          contentElement.style.height =
            `${contentElement.scrollHeight}px`;
        });

        return;
      }

      contentElement.style.height =
        `${contentElement.scrollHeight}px`;

      requestAnimationFrame(() => {
        contentElement.classList.remove('is-open');
        contentElement.style.height = '0px';
      });

      window.setTimeout(() => {
        if (
          toggleElement.getAttribute(
            'aria-expanded'
          ) === 'false'
        ) {
          contentElement.hidden = true;
        }
      }, 250);
    }

    function update() {
      const selectedVariant =
        variantMap[String(variantInput.value)];

      const selectedVariantIsEligible =
        Boolean(selectedVariant?.eligible);

      progressElement.hidden = false;

      textElement.textContent =
        getProgressText(
          cartDiscountQuantity
        );

      const shouldOpenAutomatically =
        cartDiscountQuantity > 0 ||
        selectedVariantIsEligible;

      if (
        shouldOpenAutomatically &&
        !manuallyClosed
      ) {
        setOpen(true);
      }

      if (!shouldOpenAutomatically) {
        setOpen(false);
        manuallyClosed = false;
      }
    }

    toggleElement.addEventListener(
      'click',
      () => {
        const isOpen =
          toggleElement.getAttribute(
            'aria-expanded'
          ) === 'true';

        manuallyClosed = isOpen;
        setOpen(!isOpen);
      }
    );
    // MANTRAROMA CUSTOM
    // Höhe nach Öffnen/Schließen des Rabatt-Hinweises neu berechnen
    const returnNoteElement =
      progressElement.querySelector(
        '.mantraroma-discount-progress-product__return-note'
      );

    if (returnNoteElement) {
      returnNoteElement.addEventListener(
        'toggle',
        () => {
          if (
            toggleElement.getAttribute(
              'aria-expanded'
            ) === 'true'
          ) {
            contentElement.style.height =
              `${contentElement.scrollHeight}px`;
          }
        }
      );
    }
    variantInput.addEventListener(
      'change',
      () => {
        manuallyClosed = false;
        update();
      }
    );

    new MutationObserver(() => {
      manuallyClosed = false;
      update();
    }).observe(variantInput, {
      attributes: true,
      attributeFilter: ['value']
    });

    update();

    return {
      update
    };
  }

  function updateCartDisplays() {
    const cartProgressElements =
      document.querySelectorAll(
        SELECTORS.cartProgress
      );

    cartProgressElements.forEach(
      (progressElement) => {
        const textElement =
          progressElement.querySelector(
            SELECTORS.cartText
          );

        if (!textElement) {
          return;
        }

        textElement.textContent =
          getProgressText(
            cartDiscountQuantity
          );
      }
    );

    applyCartCollapsedState();
  }

  function updateAllDisplays() {
    productProgressInstances.forEach(
      (instance) => {
        instance.update();
      }
    );

    updateCartDisplays();

    document.dispatchEvent(
      new CustomEvent(
        'mantraroma:discount-state-updated',
        {
          detail: {
            quantity:
              cartDiscountQuantity
          }
        }
      )
    );
  }

  async function refreshDiscountState() {
    if (refreshRequest) {
      return refreshRequest;
    }

    refreshRequest = fetchDiscountState()
      .then((quantity) => {
        cartDiscountQuantity = quantity;
        updateAllDisplays();
      })
      .catch((error) => {
        console.error(
          'MANTRAROMA Rabattfortschritt:',
          error
        );

        updateAllDisplays();
      })
      .finally(() => {
        refreshRequest = null;
      });

    return refreshRequest;
  }

  function initialiseProductDisplays() {
    const progressElements =
      document.querySelectorAll(
        SELECTORS.productProgress
      );

    productProgressInstances =
      Array.from(progressElements)
        .map(createProductProgress)
        .filter(Boolean);
  }

  async function initialise() {
    initialiseProductDisplays();

    document.addEventListener(
      'click',
      handleCartToggleClick
    );

    document.addEventListener(
      'on:cart:after-merge',
      refreshDiscountState
    );

    applyCartCollapsedState();

    await refreshDiscountState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialise
    );
  } else {
    initialise();
  }
})();